import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, FolderKanban, Plus, Check, Calendar, Trash2, ChevronRight, Circle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newProject, setNewProject] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [newTask, setNewTask] = useState('');

  useEffect(() => {
    if (hasAccess) {
      const saved = localStorage.getItem('home-projects');
      if (saved) {
        setProjects(JSON.parse(saved));
      }
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const saveProjects = (updated) => {
    setProjects(updated);
    localStorage.setItem('home-projects', JSON.stringify(updated));
  };

  const addProject = () => {
    if (!newProject.trim()) return;
    const project = {
      id: Date.now(),
      title: newProject,
      tasks: [],
      createdAt: new Date().toISOString(),
      status: 'active'
    };
    saveProjects([...projects, project]);
    setNewProject('');
    toast.success('Projekt skapat!');
  };

  const deleteProject = (projectId) => {
    saveProjects(projects.filter(p => p.id !== projectId));
    if (selectedProject?.id === projectId) {
      setSelectedProject(null);
    }
    toast.success('Projekt borttaget');
  };

  const addTaskToProject = (projectId) => {
    if (!newTask.trim()) return;
    const task = {
      id: Date.now(),
      title: newTask,
      done: false
    };
    const updated = projects.map(p => {
      if (p.id === projectId) {
        return { ...p, tasks: [...p.tasks, task] };
      }
      return p;
    });
    saveProjects(updated);
    setSelectedProject(updated.find(p => p.id === projectId));
    setNewTask('');
  };

  const toggleTask = (projectId, taskId) => {
    const updated = projects.map(p => {
      if (p.id === projectId) {
        return {
          ...p,
          tasks: p.tasks.map(t => t.id === taskId ? { ...t, done: !t.done } : t)
        };
      }
      return p;
    });
    saveProjects(updated);
    setSelectedProject(updated.find(p => p.id === projectId));
  };

  const deleteTask = (projectId, taskId) => {
    const updated = projects.map(p => {
      if (p.id === projectId) {
        return { ...p, tasks: p.tasks.filter(t => t.id !== taskId) };
      }
      return p;
    });
    saveProjects(updated);
    setSelectedProject(updated.find(p => p.id === projectId));
  };

  const getProgress = (project) => {
    if (project.tasks.length === 0) return 0;
    const done = project.tasks.filter(t => t.done).length;
    return Math.round((done / project.tasks.length) * 100);
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="projects-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/home')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Projekt</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att få tillgång till projekt.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  // Project detail view
  if (selectedProject) {
    const progress = getProgress(selectedProject);
    return (
      <div className="min-h-screen bg-background" data-testid="projects-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => setSelectedProject(null)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">{selectedProject.title}</h1>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
          {/* Progress */}
          <div className="bg-amber-50 rounded-2xl p-6">
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold">Framsteg</span>
              <span className="text-amber-600 font-bold">{progress}%</span>
            </div>
            <div className="h-3 bg-amber-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-amber-500 transition-all duration-300 rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              {selectedProject.tasks.filter(t => t.done).length} av {selectedProject.tasks.length} uppgifter klara
            </p>
          </div>

          {/* Add task */}
          <div className="bg-white rounded-2xl p-4 border border-border/30 space-y-3">
            <p className="text-sm font-medium text-foreground">Skriv en ny uppgift:</p>
            <div className="flex gap-2">
              <Input
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                placeholder="T.ex. Köp material, måla väggen ..."
                onKeyPress={(e) => e.key === 'Enter' && addTaskToProject(selectedProject.id)}
                className="rounded-xl flex-1"
                data-testid="project-task-input"
              />
              <Button onClick={() => addTaskToProject(selectedProject.id)} className="rounded-xl px-4" data-testid="add-project-task-btn">
                <Plus className="w-4 h-4 mr-2" />
                Lägg till
              </Button>
            </div>
          </div>

          {/* Tasks */}
          <div className="space-y-2">
            {selectedProject.tasks.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Inga uppgifter än. Lägg till din första!</p>
            ) : (
              selectedProject.tasks.map(task => (
                <div
                  key={task.id}
                  className={`bg-white p-4 rounded-xl border flex items-center gap-3 ${task.done ? 'bg-green-50 border-green-200' : 'border-border/30'}`}
                >
                  <button
                    onClick={() => toggleTask(selectedProject.id, task.id)}
                    className={`w-6 h-6 rounded-full flex items-center justify-center ${task.done ? 'text-green-600' : 'text-gray-300'}`}
                  >
                    {task.done ? <CheckCircle2 className="w-6 h-6" /> : <Circle className="w-6 h-6" />}
                  </button>
                  <span className={`flex-1 ${task.done ? 'line-through text-muted-foreground' : ''}`}>
                    {task.title}
                  </span>
                  <button
                    onClick={() => deleteTask(selectedProject.id, task.id)}
                    className="p-1 text-gray-400 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="projects-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/home')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-light">Projekt</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="text-center space-y-2">
          <FolderKanban className="w-12 h-12 mx-auto text-amber-500" />
          <h2 className="font-heading text-2xl font-semibold">Hemprojekt</h2>
          <p className="text-muted-foreground">Planera och följ dina projekt i hemmet</p>
        </div>

        {/* Add project */}
        <div className="bg-white rounded-2xl p-4 border border-border/30 space-y-3">
          <p className="text-sm font-medium text-foreground">Skapa ett nytt projekt:</p>
          <div className="flex gap-2">
            <Input
              value={newProject}
              onChange={(e) => setNewProject(e.target.value)}
              placeholder="T.ex. Renovera badrummet, bygga altan ..."
              onKeyPress={(e) => e.key === 'Enter' && addProject()}
              className="rounded-xl flex-1"
              data-testid="project-input"
            />
            <Button onClick={addProject} className="rounded-xl px-4" data-testid="add-project-btn">
              <Plus className="w-4 h-4 mr-2" />
              Lägg till
            </Button>
          </div>
        </div>

        {/* Projects list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-amber-200 border-t-amber-500 rounded-full animate-spin" />
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Inga projekt än. Skapa ditt första!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {projects.map(project => {
              const progress = getProgress(project);
              return (
                <button
                  key={project.id}
                  onClick={() => setSelectedProject(project)}
                  className="w-full bg-white p-4 rounded-xl border border-border/30 text-left hover:shadow-md transition-all group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center">
                      <FolderKanban className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold">{project.title}</h3>
                      <div className="flex items-center gap-3 mt-1">
                        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden max-w-32">
                          <div 
                            className="h-full bg-amber-500 rounded-full"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground">{progress}%</span>
                        <span className="text-xs text-muted-foreground">
                          {project.tasks.length} uppgifter
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Tips */}
        <div className="bg-amber-50 rounded-2xl p-4 border border-amber-100">
          <p className="text-amber-800 text-sm">
            💡 <strong>Tips:</strong> Dela upp stora projekt i mindre uppgifter för att hålla motivationen uppe!
          </p>
        </div>
      </main>
    </div>
  );
}
